import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faRotateLeft, faSearch, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";

function Field({ label, required = false, children, helper }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {helper ? <p className="mt-2 text-xs text-slate-500">{helper}</p> : null}
    </div>
  );
}

const inputClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-slate-400 disabled:cursor-not-allowed disabled:bg-slate-50";

export default function AnalyticsFilterPanel({
  filters,
  statusOptions,
  organizations,
  employees,
  customers,
  customerUsers,
  loading,
  customerUsersLoading,
  error,
  onFilterChange,
  onReset,
  onSubmit,
  onClose,
}) {
  return (
    <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Report Filters</h2>
          <p className="mt-1 text-sm text-slate-500">
            Slice the admin analytics by organization, customer, customer user, assigned employee, date, and status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white">
            <FontAwesomeIcon icon={faFilter} />
          </div>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Start Date" required>
          <input
            type="text"
            value={filters.startDate}
            onChange={(event) => onFilterChange("startDate", event.target.value)}
            placeholder="dd-mm-yyyy"
            inputMode="numeric"
            maxLength={10}
            className={inputClassName}
          />

        </Field>

        <Field label="End Date" required>
          <input
            type="text"
            value={filters.endDate}
            onChange={(event) => onFilterChange("endDate", event.target.value)}
            placeholder="dd-mm-yyyy"
            inputMode="numeric"
            maxLength={10}
            className={inputClassName}
          />

        </Field>

        <Field label="Status">
          <select
            value={filters.status}
            onChange={(event) => onFilterChange("status", event.target.value)}
            className={inputClassName}
          >
            {statusOptions.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Organization">
          <select
            value={filters.organizationId}
            onChange={(event) => onFilterChange("organizationId", event.target.value)}
            disabled={loading}
            className={inputClassName}
          >
            <option value="">All Organizations</option>
            {organizations.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Assigned Employee">
          <select
            value={filters.employeeUserId}
            onChange={(event) => onFilterChange("employeeUserId", event.target.value)}
            disabled={loading}
            className={inputClassName}
          >
            <option value="">All Employees</option>
            {employees.map((item) => (
              <option key={item.userId || item.id} value={item.userId}>
                {item.fullName}
                {item.employeeCode ? ` (${item.employeeCode})` : item.employeeNumber ? ` (${item.employeeNumber})` : ""}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Customer">
          <select
            value={filters.customerId}
            onChange={(event) => onFilterChange("customerId", event.target.value)}
            disabled={loading}
            className={inputClassName}
          >
            <option value="">All Customers</option>
            {customers.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
                {item.customerCode ? ` (${item.customerCode})` : ""}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Customer User" helper={!filters.customerId && !filters.organizationId && !filters.employeeUserId ? "Choose a customer or scope first for faster loading." : ""}>
          <select
            value={filters.customerUserId}
            onChange={(event) => onFilterChange("customerUserId", event.target.value)}
            disabled={loading || customerUsersLoading || (!filters.customerId && !filters.organizationId && !filters.employeeUserId)}
            className={inputClassName}
          >
            <option value="">
              {!filters.customerId && !filters.organizationId && !filters.employeeUserId
                ? "Select customer scope first"
                : customerUsersLoading
                  ? "Loading customer users..."
                  : "All Customer Users"}
            </option>
            {customerUsers.map((item) => (
              <option key={`${item.id}-${item.userId}`} value={item.userId}>
                {item.fullName}
                {item.email ? ` - ${item.email}` : ""}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <button
          type="button"
          onClick={onReset}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 sm:w-auto"
        >
          <FontAwesomeIcon icon={faRotateLeft} />
          Reset
        </button>

        <button
          type="button"
          onClick={onSubmit}
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
        >
          <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
          {loading ? "Loading Analytics..." : "Run Analytics"}
        </button>
      </div>
    </section>
  );
}
